import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Flex, Spacer, useToast, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { pureCreateMeeting } from '@/backend/pureCreateMeeting';
import { MeetingTypes } from '@/features/caseCluster/caseCluster.types';
import { useUserAuthenticationContext } from '@/features/userAuth/context/UserAuthenticationContext';
import { CaseCluster, CreateMeetingInput, Meeting, UpdateMeetingInput, User } from '@/services/API';
import useMeetingUserAccessForMeeting from '@/services/hooks/useMeetingUserAccessForMeeting';
import { updateAMeeting } from '@/store/thunk/meeting';
import { DEFAULT_HOST_NAME, DEFAULT_HOST_VALUE } from '@/utils/constants/app.constants';
import { IsLocalHost } from '@/utils/helpers/isLocalHost';
import { CreateMeetingFormColumnOne } from './components/CreateMeetingFormColumnOne';
import { CreateMeetingFormColumnTwo } from './components/CreateMeetingFormColumnTwo';
import { CreateMeetingFormData, createMeetingFormSchema } from './createMeetingFormSchema';

type Props = {
  userList: User[];
  caseClusterlist: CaseCluster[];
  meeting?: Meeting; // if we have a meeting, we are in edition mode or in duplicate mode
  isDuplicateMeeting?: boolean;
  closeMeetingForm: () => void;
};

const generateCode = () => {
  //random string of 6 characters
  return Math.random().toString(36).substring(2, 8);
};

export const CreateMeetingForm = ({
  userList,
  caseClusterlist,
  meeting,
  isDuplicateMeeting,
  closeMeetingForm,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useUserAuthenticationContext();
  const { userAccessList } = useMeetingUserAccessForMeeting(meeting?.id);

  const toast = useToast();
  const [isColumnOneValid, setFirstColumnValid] = useState<boolean>(false);
  const form = useForm<CreateMeetingFormData>({
    resolver: zodResolver(createMeetingFormSchema),
    defaultValues: {
      meetingType: (meeting?.meetingType as MeetingTypes) || MeetingTypes.LIVE,
      name: meeting?.name || '',
      eventDate: meeting?.eventDate ? new Date(meeting.eventDate) : new Date(),
      location: meeting?.eventLocation || '',
      selectedClientID: undefined,
      meetingClientIDs: meeting?.clientIDs || [],

      selectedTeamID: undefined,
      meetingTeamIDs: meeting?.teamIDs || [],
      caseClusterID: meeting?.caseClusterID || '',
      hostUserID: meeting?.hostID || (IsLocalHost() ? user?.id : DEFAULT_HOST_VALUE),
      hostFullName: meeting?.hostFullName || (IsLocalHost() ? DEFAULT_HOST_NAME : ''),
      purchaseOrderNbr: meeting?.purchaseOrderNbr || (IsLocalHost() ? 'PO-111' : ''),
      customNotes: meeting?.meetingNotes || '',
      isStreamingHost: meeting?.isStreamingHost || false,
      isDemo: meeting?.isDemo || false,
    },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  const onSubmit = useCallback(
    async (values: CreateMeetingFormData) => {
      if (values.meetingType === MeetingTypes.LIVE && values.meetingClientIDs.length === 0) {
        toast({
          title: 'Error',
          description: t('admin.createMeeting.form.meetingClients.live.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }
      if (values.meetingType === MeetingTypes.LIVE && values.meetingTeamIDs?.length === 0) {
        toast({
          title: 'Error',
          description: t('admin.createMeeting.form.meetingTeams.live.error'),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (!meeting || isDuplicateMeeting) {
        const meetingInput: CreateMeetingInput = {
          name: values.name,
          caseClusterID: values.caseClusterID,
          hostID: values.hostUserID,
          hostFullName: values.hostFullName,
          eventDate: values.eventDate.toISOString(),
          eventLocation: values.location,
          clientIDs: values.meetingClientIDs,
          purchaseOrderNbr: values.purchaseOrderNbr || '',
          meetingNotes: values.customNotes || '',
          currentSlide: 0,
          isMock: false,
          isInTesting: false,
          isArchived: false,
          meetingType: values.meetingType,
          isDemo: values.isDemo,
          isStreamingHost: values.isStreamingHost,
          teamIDs: values.meetingTeamIDs,
        };
        if (values.generateGuestUrl) meetingInput.guestUrlCode = generateCode();
        await pureCreateMeeting(
          meetingInput,
          isDuplicateMeeting ? userAccessList.map((val) => val.userID) : undefined
        ).finally(() => {
          closeMeetingForm();
        });
      } else {
        const meetingUpdate: UpdateMeetingInput = {
          id: meeting.id,
          name: values.name,
          caseClusterID: values.caseClusterID,
          hostID: values.hostUserID,
          hostFullName: values.hostFullName,
          eventDate: values.eventDate.toISOString(),
          eventLocation: values.location,
          clientIDs: values.meetingClientIDs,
          purchaseOrderNbr: values.purchaseOrderNbr,
          meetingNotes: values.customNotes,
          meetingType: values.meetingType,
          isStreamingHost: values.isStreamingHost,
          teamIDs: values.meetingTeamIDs,
        };
        if (values.generateGuestUrl) {
          meetingUpdate.guestUrlCode = generateCode();
        }
        await updateAMeeting(meetingUpdate).finally(() => {
          closeMeetingForm();
        });
      }
    },
    [closeMeetingForm, meeting, t, toast, userAccessList, isDuplicateMeeting]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset disabled={isSubmitting}>
        <Flex justifyContent="space-between" overflowY="auto" overflowX="hidden" padding={1}>
          <VStack gap={2} w="full" maxW="300px">
            <CreateMeetingFormColumnOne
              form={form}
              meeting={meeting}
              setFirstColumnValid={setFirstColumnValid}
              caseClusterList={caseClusterlist}
              isDuplicateMeeting={isDuplicateMeeting}
            />
          </VStack>
          <Divider h="full" w="2px" orientation="vertical" />
          <fieldset
            disabled={!isColumnOneValid}
            style={{
              width: '100%',
              maxWidth: '300px',
            }}
          >
            <VStack gap={2} w="full" opacity={isColumnOneValid ? 1 : 0.5}>
              <CreateMeetingFormColumnTwo
                form={form}
                meeting={meeting}
                userList={userList}
                isDuplicateMeeting={isDuplicateMeeting}
              />
            </VStack>
          </fieldset>
        </Flex>

        <Spacer h={4} />

        <Button
          type="submit"
          p="2"
          width="full"
          background="blue.400"
          color="white"
          variant="solid"
          isDisabled={isSubmitting}
          isLoading={isSubmitting}
          aria-label="Submit form"
        >
          {meeting && isDuplicateMeeting && t('common.duplicate')}
          {meeting && !isDuplicateMeeting && t('common.update')}
          {!meeting && t('common.create')}
        </Button>
      </fieldset>
    </form>
  );
};
